import React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { NewAuditLogRecord } from './AuditLogPanel.types';
import { flattenDiff } from './AuditLogPanel.utils';
import AuditLogCellValue from './AuditLogCellValue';

interface Props {
	record: NewAuditLogRecord & { type: 'updated' };
	hiddenFields: string[];
}

const useStyles = makeStyles(() => ({
	table: {
		tableLayout: 'fixed',
		'& tr:last-child td': { borderBottom: 'none' },
	},
	cell: {
		padding: '5px 8px',
		borderColor: 'oklch(93% 0.005 85)',
		verticalAlign: 'top',
	},
	pathCell: {
		width: '50%',
		fontFamily: '"Roboto Mono", "Courier New", monospace',
		fontSize: '1.1rem',
		color: 'oklch(55% 0.008 55)',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		direction: 'rtl',
		textAlign: 'left',
	},
	valueCell: {
		textAlign: 'right',
		fontSize: '1.3rem',
		color: 'oklch(35% 0.010 55)',
	},
	rowAdded: {
		backgroundColor: 'oklch(97% 0.022 145)',
	},
	rowRemoved: {
		backgroundColor: 'oklch(97.5% 0.016 22)',
	},
	before: {
		display: 'block',
		color: 'oklch(41% 0.09 22)',
		textDecoration: 'line-through',
		opacity: 0.8,
	},
	after: {
		display: 'block',
		color: 'oklch(37% 0.09 145)',
	},
}));

function AuditLogDiffView({ record, hiddenFields }: Props) {
	const classes = useStyles();
	const entries = flattenDiff(record.diff).filter(
		e => !hiddenFields.includes(e.path.split(' > ')[0])
	);

	if (entries.length === 0) return null;

	return (
		<Table className={classes.table} size="small">
			<TableBody>
				{entries.map((entry, i) => {
					const isAdded = entry.before === null;
					const isRemoved = entry.after === null;
					const rowClass = isAdded ? classes.rowAdded : isRemoved ? classes.rowRemoved : '';
					return (
						<TableRow key={`${entry.path}-${i}`} className={rowClass}>
							<TableCell className={`${classes.cell} ${classes.pathCell}`} title={entry.path}>
								{entry.path}
							</TableCell>
							<TableCell className={`${classes.cell} ${classes.valueCell}`}>
								{isAdded || isRemoved ? (
									<AuditLogCellValue value={entry.before ?? entry.after} />
								) : (
									<>
										<span className={classes.before}>
											<AuditLogCellValue value={entry.before} />
										</span>
										<span className={classes.after}>
											<AuditLogCellValue value={entry.after} />
										</span>
									</>
								)}
							</TableCell>
						</TableRow>
					);
				})}
			</TableBody>
		</Table>
	);
}

export default AuditLogDiffView;
